import { useRxCollectionPatient } from "$global-providers/rxdb-provider/collections/patient";
import type { PatientInsurance } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { useEffect, useState } from "react";

/**
 * Reads a patient's `insurance` subdoc from the rxdb patient collection
 * and adapts it to the `PatientInsurance` shape consumed by the v2
 * insurer accordion.
 *
 * Returns `null` while loading or if the patient or insurance is not
 * yet present in the local store — the insurer accordion treats this
 * as "no pre-fill", which is the same behaviour as the legacy flow
 * when the patient has no insurance recorded.
 */
export function useFetchPatientInsuranceRxdb(
  patientId: string | undefined,
): PatientInsurance | null {
  const collection = useRxCollectionPatient();
  const [insurance, setInsurance] = useState<PatientInsurance | null>(null);

  useEffect(() => {
    if (!collection || !patientId) {
      setInsurance(null);
      return;
    }
    const sub = collection.findOne(patientId).$.subscribe((doc) => {
      const ins = doc?.insurance;
      if (!ins) {
        setInsurance(null);
        return;
      }
      setInsurance({
        insuranceType: ins.type ?? "",
        // The rxdb shape stores `details` as a loose object; the v2
        // insurer accordion treats it as `InsuranceDetails` with
        // optional fields and uses `?.` throughout.
        insuranceDetails:
          (ins.details as unknown as PatientInsurance["insuranceDetails"]) ??
          ({} as PatientInsurance["insuranceDetails"]),
      });
    });
    return () => sub.unsubscribe();
  }, [collection, patientId]);

  return insurance;
}
